import { useState } from 'react'
import { X, Building2, CreditCard, UserPlus, Rocket, ChevronLeft, ChevronRight, Check, Loader2 } from 'lucide-react'
import { supabase } from '@/lib/supabase'
import type { Lead } from '@/types'

/* ─── Plans (hardcoded IDs from subscription_plans) ──────────── */

const PLANS = [
  { id: '937ac9e2-c491-4942-9124-30764ce505d6', name: 'Starter', code: 'basic', price: 9900, users: 1, contacts: 500 },
  { id: '2d2d5432-1143-4efb-bd86-c31a5a769518', name: 'Pro', code: 'pro', price: 24900, users: 3, contacts: 5000 },
  { id: 'ed957bbf-f0fb-4506-aa60-6001d8a15017', name: 'Business', code: 'business', price: 49900, users: 20, contacts: null },
]

const TRIAL_OPTIONS = [
  { days: 7, label: '7 days' },
  { days: 14, label: '14 days' },
]

const ORG_TYPES = [
  { value: 'individual', label: 'Individual' },
  { value: 'osek', label: 'Osek Murshe' },
  { value: 'company', label: 'Company' },
]

function formatNIS(agorot: number) {
  return `₪${(agorot / 100).toLocaleString()}`
}

/* ─── Step components ─────────────────────────────────────────── */

interface StepProps {
  form: WizardForm
  setForm: (fn: (prev: WizardForm) => WizardForm) => void
}

interface WizardForm {
  org_name: string
  org_email: string
  org_phone: string
  org_type: string
  plan_id: string
  trial_days: number
  admin_name: string
  admin_email: string
}

function StepOrg({ form, setForm }: StepProps) {
  const set = (key: keyof WizardForm, value: string) => setForm(f => ({ ...f, [key]: value }))
  return (
    <div className="space-y-4">
      <div>
        <label className="text-xs font-medium mb-1 block" style={{ color: '#9ca3af' }}>Organization name *</label>
        <input value={form.org_name} onChange={e => set('org_name', e.target.value)}
          className="w-full px-3 py-2.5 rounded-lg text-sm text-white outline-none focus:ring-2 focus:ring-indigo-500"
          style={{ background: '#252836', border: '1px solid #3f4258' }} />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-medium mb-1 block" style={{ color: '#9ca3af' }}>Email</label>
          <input value={form.org_email} onChange={e => set('org_email', e.target.value)}
            className="w-full px-3 py-2.5 rounded-lg text-sm text-white outline-none focus:ring-2 focus:ring-indigo-500"
            style={{ background: '#252836', border: '1px solid #3f4258' }} />
        </div>
        <div>
          <label className="text-xs font-medium mb-1 block" style={{ color: '#9ca3af' }}>Phone</label>
          <input value={form.org_phone} onChange={e => set('org_phone', e.target.value)}
            className="w-full px-3 py-2.5 rounded-lg text-sm text-white outline-none focus:ring-2 focus:ring-indigo-500"
            style={{ background: '#252836', border: '1px solid #3f4258' }} />
        </div>
      </div>
      <div>
        <label className="text-xs font-medium mb-1 block" style={{ color: '#9ca3af' }}>Business type</label>
        <div className="flex gap-2">
          {ORG_TYPES.map(t => (
            <button key={t.value} onClick={() => set('org_type', t.value)}
              className="flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-all"
              style={{
                background: form.org_type === t.value ? '#6366f120' : '#252836',
                color: form.org_type === t.value ? '#a5b4fc' : '#9ca3af',
                border: `1px solid ${form.org_type === t.value ? '#6366f140' : '#3f4258'}`,
              }}>
              {t.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}

function StepPlan({ form, setForm }: StepProps) {
  return (
    <div className="space-y-4">
      <div>
        <label className="text-xs font-medium mb-2 block" style={{ color: '#9ca3af' }}>Select plan</label>
        <div className="space-y-2">
          {PLANS.map(p => {
            const active = form.plan_id === p.id
            return (
              <button key={p.id} onClick={() => setForm(f => ({ ...f, plan_id: p.id }))}
                className="w-full text-left px-4 py-3 rounded-xl transition-all flex items-center justify-between"
                style={{
                  background: active ? '#6366f120' : '#252836',
                  border: `1px solid ${active ? '#6366f140' : '#3f4258'}`,
                }}>
                <div>
                  <div className="text-sm font-semibold" style={{ color: active ? '#a5b4fc' : 'white' }}>{p.name}</div>
                  <div className="text-xs mt-0.5" style={{ color: '#9ca3af' }}>
                    {p.users} users · {p.contacts ? `${p.contacts.toLocaleString()} contacts` : 'Unlimited contacts'}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-sm font-bold" style={{ color: active ? '#a5b4fc' : 'white' }}>{formatNIS(p.price)}/mo</div>
                  {active && <Check size={16} className="ml-auto mt-1" style={{ color: '#6366f1' }} />}
                </div>
              </button>
            )
          })}
        </div>
      </div>
      <div>
        <label className="text-xs font-medium mb-2 block" style={{ color: '#9ca3af' }}>Trial period</label>
        <div className="flex gap-2">
          {TRIAL_OPTIONS.map(t => (
            <button key={t.days} onClick={() => setForm(f => ({ ...f, trial_days: t.days }))}
              className="flex-1 px-3 py-2.5 rounded-lg text-sm font-medium transition-all"
              style={{
                background: form.trial_days === t.days ? '#22c55e20' : '#252836',
                color: form.trial_days === t.days ? '#22c55e' : '#9ca3af',
                border: `1px solid ${form.trial_days === t.days ? '#22c55e40' : '#3f4258'}`,
              }}>
              {t.label}
            </button>
          ))}
        </div>
        <p className="text-xs mt-1.5" style={{ color: '#6b7280' }}>
          Trial expires {new Date(Date.now() + form.trial_days * 86400000).toLocaleDateString()}
        </p>
      </div>
    </div>
  )
}

function StepUser({ form, setForm }: StepProps) {
  const set = (key: keyof WizardForm, value: string) => setForm(f => ({ ...f, [key]: value }))
  return (
    <div className="space-y-4">
      <p className="text-sm" style={{ color: '#9ca3af' }}>
        An invite email will be sent to this address. The user will confirm their email and set a password to access JumpStart.
      </p>
      <div>
        <label className="text-xs font-medium mb-1 block" style={{ color: '#9ca3af' }}>Admin name *</label>
        <input value={form.admin_name} onChange={e => set('admin_name', e.target.value)}
          className="w-full px-3 py-2.5 rounded-lg text-sm text-white outline-none focus:ring-2 focus:ring-indigo-500"
          style={{ background: '#252836', border: '1px solid #3f4258' }} />
      </div>
      <div>
        <label className="text-xs font-medium mb-1 block" style={{ color: '#9ca3af' }}>Admin email *</label>
        <input type="email" value={form.admin_email} onChange={e => set('admin_email', e.target.value)}
          className="w-full px-3 py-2.5 rounded-lg text-sm text-white outline-none focus:ring-2 focus:ring-indigo-500"
          style={{ background: '#252836', border: '1px solid #3f4258' }} />
      </div>
    </div>
  )
}

function StepReview({ form }: { form: WizardForm }) {
  const plan = PLANS.find(p => p.id === form.plan_id)
  return (
    <div className="space-y-3">
      <div className="rounded-xl p-4" style={{ background: '#252836', border: '1px solid #3f4258' }}>
        <div className="text-xs font-medium mb-2" style={{ color: '#9ca3af' }}>Organization</div>
        <div className="text-white font-semibold">{form.org_name}</div>
        <div className="text-xs mt-1" style={{ color: '#9ca3af' }}>
          {form.org_email && <span>{form.org_email} · </span>}
          {form.org_phone && <span>{form.org_phone} · </span>}
          {ORG_TYPES.find(t => t.value === form.org_type)?.label}
        </div>
      </div>
      <div className="rounded-xl p-4" style={{ background: '#252836', border: '1px solid #3f4258' }}>
        <div className="text-xs font-medium mb-2" style={{ color: '#9ca3af' }}>Plan & Trial</div>
        <div className="flex items-center justify-between">
          <div className="text-white font-semibold">{plan?.name ?? '—'}</div>
          <div className="text-sm" style={{ color: '#a5b4fc' }}>{plan ? formatNIS(plan.price) + '/mo' : ''}</div>
        </div>
        <div className="text-xs mt-1" style={{ color: '#22c55e' }}>
          {form.trial_days}-day trial → expires {new Date(Date.now() + form.trial_days * 86400000).toLocaleDateString()}
        </div>
      </div>
      <div className="rounded-xl p-4" style={{ background: '#252836', border: '1px solid #3f4258' }}>
        <div className="text-xs font-medium mb-2" style={{ color: '#9ca3af' }}>Admin User</div>
        <div className="text-white font-semibold">{form.admin_name}</div>
        <div className="text-xs mt-1" style={{ color: '#9ca3af' }}>{form.admin_email}</div>
        <div className="text-xs mt-1" style={{ color: '#f59e0b' }}>Invite email will be sent automatically</div>
      </div>
      {form.org_phone && (
        <div className="rounded-xl p-4" style={{ background: '#252836', border: '1px solid #3f4258' }}>
          <div className="text-xs font-medium mb-2" style={{ color: '#9ca3af' }}>Notifications</div>
          <div className="text-xs" style={{ color: '#22c55e' }}>✓ Welcome WhatsApp to {form.org_phone}</div>
          <div className="text-xs mt-0.5" style={{ color: '#22c55e' }}>✓ You'll get a WhatsApp notification</div>
        </div>
      )}
    </div>
  )
}

/* ─── Wizard ──────────────────────────────────────────────────── */

const STEPS = [
  { title: 'Organization', icon: Building2 },
  { title: 'Plan & Trial', icon: CreditCard },
  { title: 'Admin User', icon: UserPlus },
  { title: 'Review', icon: Rocket },
]

interface Props {
  lead: Lead
  onClose: () => void
  onDone: () => void
}

export default function ConvertWizard({ lead, onClose, onDone }: Props) {
  const [step, setStep] = useState(0)
  const [submitting, setSubmitting] = useState(false)
  const [result, setResult] = useState<{ success: boolean; org_id?: string; error?: string } | null>(null)

  const [form, setForm] = useState<WizardForm>({
    org_name: lead.company_name || lead.name,
    org_email: lead.email || '',
    org_phone: lead.phone || '',
    org_type: 'osek',
    plan_id: PLANS[0].id, // Default to Starter
    trial_days: 14,
    admin_name: lead.name,
    admin_email: lead.email || '',
  })

  const canNext = () => {
    if (step === 0) return form.org_name.trim().length > 0
    if (step === 1) return !!form.plan_id
    if (step === 2) return form.admin_name.trim().length > 0 && form.admin_email.includes('@')
    return true
  }

  const handleSubmit = async () => {
    setSubmitting(true)
    try {
      const { data, error } = await supabase.functions.invoke('jumpstart-onboard-org', {
        body: {
          lead_id: lead.id,
          org_name: form.org_name,
          org_email: form.org_email || null,
          org_phone: form.org_phone || null,
          org_type: form.org_type,
          plan_id: form.plan_id,
          trial_days: form.trial_days,
          admin_name: form.admin_name,
          admin_email: form.admin_email,
        },
      })

      if (error) throw error
      setResult({ success: true, org_id: data.org_id })
    } catch (err: any) {
      console.error('Onboard failed:', err)
      setResult({ success: false, error: err.message || 'Unknown error' })
    }
    setSubmitting(false)
  }

  // Success screen
  if (result?.success) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.6)' }}>
        <div className="w-full max-w-md rounded-xl p-8 text-center" style={{ background: '#1a1d27', border: '1px solid #2d3148' }}>
          <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: '#22c55e20' }}>
            <Check size={32} style={{ color: '#22c55e' }} />
          </div>
          <h3 className="text-xl font-bold text-white mb-2">Organization Created!</h3>
          <p className="text-sm mb-1" style={{ color: '#9ca3af' }}>
            <strong className="text-white">{form.org_name}</strong> is now live in JumpStart
          </p>
          <p className="text-xs mb-4" style={{ color: '#6b7280' }}>
            Invite email sent to {form.admin_email}
          </p>
          <div className="flex gap-2 justify-center">
            <button onClick={() => { onDone(); onClose() }}
              className="px-4 py-2 rounded-lg text-sm font-medium text-white"
              style={{ background: '#6366f1' }}>
              Back to Leads
            </button>
            <button onClick={() => window.open(`/organizations/${result.org_id}`, '_blank')}
              className="px-4 py-2 rounded-lg text-sm font-medium"
              style={{ color: '#a5b4fc', background: '#6366f120' }}>
              View Org →
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Error screen
  if (result && !result.success) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.6)' }}>
        <div className="w-full max-w-md rounded-xl p-8 text-center" style={{ background: '#1a1d27', border: '1px solid #2d3148' }}>
          <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: '#ef444420' }}>
            <X size={32} style={{ color: '#ef4444' }} />
          </div>
          <h3 className="text-xl font-bold text-white mb-2">Something went wrong</h3>
          <p className="text-sm mb-4" style={{ color: '#9ca3af' }}>{result.error}</p>
          <div className="flex gap-2 justify-center">
            <button onClick={() => setResult(null)}
              className="px-4 py-2 rounded-lg text-sm font-medium text-white"
              style={{ background: '#6366f1' }}>
              Try Again
            </button>
            <button onClick={onClose}
              className="px-4 py-2 rounded-lg text-sm" style={{ color: '#9ca3af' }}>
              Close
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.6)' }}>
      <div className="w-full max-w-lg rounded-xl" style={{ background: '#1a1d27', border: '1px solid #2d3148' }}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: '#2d3148' }}>
          <h3 className="text-lg font-semibold text-white">Convert to Organization</h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-white/10" style={{ color: '#9ca3af' }}><X size={18} /></button>
        </div>

        {/* Step indicator */}
        <div className="flex px-6 pt-4 pb-2 gap-1">
          {STEPS.map((s, i) => {
            const Icon = s.icon
            const isDone = i < step
            const isCurrent = i === step
            return (
              <button key={i} onClick={() => i < step && setStep(i)}
                className="flex-1 flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-xs font-medium transition-all"
                style={{
                  background: isCurrent ? '#6366f120' : isDone ? '#22c55e10' : 'transparent',
                  color: isCurrent ? '#a5b4fc' : isDone ? '#22c55e' : '#6b7280',
                }}>
                {isDone ? <Check size={12} /> : <Icon size={12} />}
                <span className="hidden sm:inline">{s.title}</span>
              </button>
            )
          })}
        </div>

        {/* Step content */}
        <div className="px-6 py-4 min-h-[280px]">
          {step === 0 && <StepOrg form={form} setForm={setForm} />}
          {step === 1 && <StepPlan form={form} setForm={setForm} />}
          {step === 2 && <StepUser form={form} setForm={setForm} />}
          {step === 3 && <StepReview form={form} />}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t" style={{ borderColor: '#2d3148' }}>
          <button onClick={() => step > 0 ? setStep(step - 1) : onClose()}
            className="flex items-center gap-1 px-3 py-2 rounded-lg text-sm"
            style={{ color: '#9ca3af' }}>
            <ChevronLeft size={14} />
            {step === 0 ? 'Cancel' : 'Back'}
          </button>

          {step < 3 ? (
            <button onClick={() => setStep(step + 1)} disabled={!canNext()}
              className="flex items-center gap-1 px-4 py-2 rounded-lg text-sm font-medium text-white disabled:opacity-50"
              style={{ background: '#6366f1' }}>
              Next <ChevronRight size={14} />
            </button>
          ) : (
            <button onClick={handleSubmit} disabled={submitting}
              className="flex items-center gap-1.5 px-5 py-2 rounded-lg text-sm font-medium text-white disabled:opacity-50"
              style={{ background: '#22c55e' }}>
              {submitting ? <><Loader2 size={14} className="animate-spin" /> Creating...</> : <><Rocket size={14} /> Create Organization</>}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
